/**
 * The onboarding memory — SPEC-09 §3.5.5, and the whole of it.
 *
 * ONE localStorage KEY, NAMESPACED BY USER ID. `chaos.onboarding.v1.<userId>`.
 * The namespace is not decoration: this box has more than one person in it, the
 * browser is shared in demos, and an un-namespaced key would tell the second
 * person they had already finished a wizard they have never seen. A missing
 * user id means we do not know whose progress this is, so nothing is written
 * and nothing is read — the wizard then behaves like a first visit, which is
 * the safe direction to be wrong in.
 *
 * NO MIGRATION, NO SERVER WRITE. That is a recorded decision (SPEC-09 §3.5.5 +
 * §10-Q5), not an omission: cross-device onboarding state would need a column,
 * and a column needs a migration, and a migration for "did you see the tour"
 * buys a schema change for a fact that costs nothing to lose. Board-scoped
 * `viewsApi` is not reusable here — it is keyed by board (`lib/api/views.ts`),
 * and onboarding is keyed by person.
 *
 * WHY THIS FILE IS DEFENSIVE ABOUT localStorage. Safari in private mode throws
 * on `setItem`; a corrupted value throws on `JSON.parse`; an extension can
 * replace the object entirely. None of those are reasons to show a person a
 * broken screen, so every access is wrapped and every failure degrades to "we
 * have no record", which is the same state as a brand-new account.
 */

/** The four cheap signals the invited-member checklist self-checks against. */
export interface OnboardingChecklist {
  board: boolean;
  card: boolean;
  transcript: boolean;
  inbox: boolean;
}

export interface OnboardingState {
  /** ISO timestamp the wizard was finished (or skipped to the end). */
  completedAt?: string;
  /** ISO timestamp the first-run checklist was dismissed. */
  dismissedChecklistAt?: string;
  /** 1-based step the wizard was last standing on, for resume. */
  step?: number;
  /** ISO timestamp the three board coach marks finished. Once, ever. */
  coachMarksSeenAt?: string;
  checklist: OnboardingChecklist;
}

export const ONBOARDING_STORE_PREFIX = 'chaos.onboarding.v1';

export const EMPTY_CHECKLIST: OnboardingChecklist = {
  board: false,
  card: false,
  transcript: false,
  inbox: false,
};

export function onboardingStorageKey(userId: string): string {
  return `${ONBOARDING_STORE_PREFIX}.${userId}`;
}

function emptyState(): OnboardingState {
  return { checklist: { ...EMPTY_CHECKLIST } };
}

/**
 * Read this person's record. Never throws, never returns null: a caller that
 * has to branch on null writes the branch once and forgets it the second time.
 */
export function readOnboarding(userId: string | null | undefined): OnboardingState {
  if (!userId) return emptyState();
  try {
    const raw = window.localStorage.getItem(onboardingStorageKey(userId));
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as Partial<OnboardingState> | null;
    if (!parsed || typeof parsed !== 'object') return emptyState();
    return {
      completedAt: typeof parsed.completedAt === 'string' ? parsed.completedAt : undefined,
      dismissedChecklistAt:
        typeof parsed.dismissedChecklistAt === 'string' ? parsed.dismissedChecklistAt : undefined,
      step: typeof parsed.step === 'number' && parsed.step > 0 ? parsed.step : undefined,
      coachMarksSeenAt:
        typeof parsed.coachMarksSeenAt === 'string' ? parsed.coachMarksSeenAt : undefined,
      checklist: {
        board: parsed.checklist?.board === true,
        card: parsed.checklist?.card === true,
        transcript: parsed.checklist?.transcript === true,
        inbox: parsed.checklist?.inbox === true,
      },
    };
  } catch {
    // Private-mode Safari, a quota wall, or somebody else's JSON in our key.
    return emptyState();
  }
}

/** Merge a patch into this person's record. A write that fails is dropped. */
export function writeOnboarding(
  userId: string | null | undefined,
  patch: Partial<OnboardingState>,
): OnboardingState {
  const next: OnboardingState = { ...readOnboarding(userId), ...patch };
  if (!userId) return next;
  try {
    window.localStorage.setItem(onboardingStorageKey(userId), JSON.stringify(next));
  } catch {
    /* no record is better than a broken screen */
  }
  return next;
}

/** Tick one checklist row. Idempotent — the rows only ever go from false to true. */
export function markChecklist(
  userId: string | null | undefined,
  row: keyof OnboardingChecklist,
): OnboardingState {
  const current = readOnboarding(userId);
  if (current.checklist[row]) return current;
  return writeOnboarding(userId, { checklist: { ...current.checklist, [row]: true } });
}

/**
 * THE ENTRY DECISION, in one place (SPEC-09 §3.5.1).
 *
 * The route guard and the sidebar item must never disagree about whether this
 * person is "done": a menu entry that leads to an immediate redirect is worse
 * than no menu entry. So both ask this function.
 */
export function isOnboardingComplete(userId: string | null | undefined): boolean {
  return typeof readOnboarding(userId).completedAt === 'string';
}

/** True while the first-run checklist should still be offered. */
export function isChecklistDismissed(userId: string | null | undefined): boolean {
  return typeof readOnboarding(userId).dismissedChecklistAt === 'string';
}
